/* eslint "@typescript-eslint/camelcase": "off" */
import AbstractDBModel from '../../model/AbstractDBModel'
import { Unsorted } from '../db/SortRequest'
import Api from './Api'

function mapDocument(document: any): any {
  const { _id, _rev, ...values } = document
  return {
    id: _id,
    rev: _rev,
    ...values,
  }
}

export default class Repository<T extends AbstractDBModel> {
  db: Api<T>

  constructor(db: Api<T>) {
    this.db = db
  }

  async find(id: string): Promise<T> {
    const document = await this.db.get(id)

    if (document?.data) {
      return mapDocument(document.data)
    } else {
      return {} as T
    }
  }

  async findAll(sort = Unsorted): Promise<T[]> {
    const selector: any = {}

    sort.sorts.forEach(s => {
      selector[s.field] = { $gt: null }
    })

    const result = await this.db.find({
      selector,
      sort: sort.sorts.length > 0 ? sort.sorts.map(s => ({ [s.field]: s.direction })) : undefined,
      limit: 1000,
    })

    return (result as any).data.docs.map(mapDocument)
  }

  async search(criteria: any): Promise<T[]> {
    const response = await this.db.find(criteria)

    if (response) {
      return (response as any).data.docs.map(mapDocument)
    } else {
      return []
    }
  }

  async searchPage(criteria: any): Promise<T> {
    const response = await this.db.find(criteria)

    if (response) {
      const docs = response.data.docs.map(mapDocument)
      const data = { ...response.data, docs }
      return data
    } else {
      return {} as T
    }
  }

  async aggregateSimple(criteria: any): Promise<T[]> {
    const response = await this.db.aggregate(criteria)

    if (response) {
      return (response as any).data.docs.map(mapDocument)
    } else {
      return []
    }
  }

  async aggregate(criteria: any, map: any): Promise<T> {
    const response = await this.db.aggregate(criteria)

    if (response) {
      const docs = response.data.docs?.map(map).filter(Boolean)
      const data = { ...response.data, docs }
      return data
    } else {
      return {} as T
    }
  }

  async aggregateAll(criteria: any, map: any): Promise<T[]> {
    const response = await this.db.aggregate(criteria)

    if (response) {
      if (!response.data.docs) {
        return []
      }
      const docs = response.data.docs.map(map)
      // const data = { ...response.data, docs }
      return docs
    } else {
      return [] as T[]
    }
  }

  async save(entity: T): Promise<T> {
    const currentTime = new Date().toISOString()

    const userSession = JSON.parse(window.sessionStorage.userSession)
    const { id, rev, ...valuesToSave } = entity
    const savedEntity = await this.db.put({
      ...valuesToSave,
      createdAt: currentTime,
      updatedAt: currentTime,
      updatedBy: userSession?.id,
    })
    return this.find((savedEntity as any).data._id)
  }

  async saveOrUpdate(entity: T): Promise<T> {
    if (!entity.id) {
      return this.save(entity)
    }

    const userSession = JSON.parse(window.sessionStorage.userSession)
    const { id, rev, ...dataToSave } = entity

    try {
      await this.find(entity.id)
      const entityToUpdate = {
        _id: id,
        _rev: rev,
        ...dataToSave,
        updatedAt: new Date().toISOString(),
        updatedBy: userSession?.id,
      }

      await this.db.put(entityToUpdate)
      return this.find(entity.id)
    } catch (error) {
      if (error.response && error.response.status === 403) {
        throw error
      } else {
        return this.save(entity)
      }
    }
  }

  async delete(entity: T): Promise<T> {
    const e = entity as any
    return mapDocument(this.db.remove(e.id, e.rev))
  }
}
